import React from 'react';
import { Calendar, CalendarDays, MapPin, Users, Plus, Bell, Settings, LogOut, BarChart3, Trophy, Download, AlertCircle, MessageSquare, DollarSign } from 'lucide-react';
import { useUnreadNotificationCount } from '../../../queries/notificationQueries';

const MobileMenu = ({
  currentUser,
  mobileMenuOpen,
  setMobileMenuOpen,
  handleLogout,
  activeTab,
  setActiveTab
}) => {
  // Get unread notification count
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const mobileMenuTabs = {
    teamrezy_employee: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'venues', label: 'Venues', icon: MapPin },
      { id: 'games', label: 'Games', icon: Trophy },
      { id: 'tournament_games', label: 'Tournament Games', icon: CalendarDays },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'adminPanel', label: 'Payments', icon: DollarSign },
      { id: 'contact', label: 'Contact Messages', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager_primary: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager_assistant: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    venue_manager: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'venues', label: 'My Venue', icon: MapPin },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ]
  };

  // Tools Section in Mobile Menu (only for test users)
  const mobileToolsItems = currentUser?.is_test_user ? (
    currentUser?.role === 'teamrezy_employee' ? [
      { id: 'teamManagers', label: 'Team Managers', icon: Users },
      { id: 'teamSearchExample', label: 'Team Search Demo', icon: Users },
      { id: 'authDiagnostic', label: 'Auth Diagnostic', icon: AlertCircle },
      { id: 'hockeyFinderImport', label: 'Hockey Finder Import', icon: Download }
    ] : [
      { id: 'authDiagnostic', label: 'Auth Diagnostic', icon: AlertCircle }
    ]
  ) : [];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setTimeout(() => {
      setMobileMenuOpen(false);
    }, 100);
  };

  if (!mobileMenuOpen) return null;

  return (
    <div className="mobile-menu bg-white border-b border-gray-200 shadow-lg">
      <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        {/* User info in mobile menu */}
        <div className="px-3 py-2 text-sm text-gray-600 border-b border-gray-200">
          {currentUser
            ? `Welcome, ${currentUser.first_name || currentUser.email}`
            : ''}
        </div>

        {/* Navigation items */}
        <div className="space-y-1 pt-2">
          {(() => {
            return (mobileMenuTabs[currentUser?.role] || []).map(tab => (
              <button
                key={tab.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleTabClick(tab.id);
                }}
                className={`w-full flex items-center space-x-3 px-3 py-3 rounded-md font-medium text-sm text-left transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-navy-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={{ border: 'none' }}
              >
                <tab.icon className="w-5 h-5 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ));
          })()}

          {/* Tools Section in Mobile Menu */}
          {mobileToolsItems.length > 0 && (
            <>
              <div className="border-t border-gray-200 mt-2 pt-2">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tools
                </div>
                {mobileToolsItems.map(item => (
                  <button
                    key={item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTabClick(item.id);
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-3 rounded-md font-medium text-sm text-left transition-colors duration-200 ${
                      activeTab === item.id
                        ? 'bg-blue-50 text-navy-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ border: 'none' }}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Mobile-only items */}
        <div className="border-t border-gray-200 pt-3 mt-3">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleTabClick('notifications');
            }}
            className="w-full flex items-center space-x-3 px-3 py-3 rounded-md font-medium text-sm text-left text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            style={{ border: 'none' }}
          >
            <div className="relative">
              <Bell className="w-5 h-5 flex-shrink-0" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <span>Notifications</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-3 py-3 rounded-md font-medium text-sm text-left text-red-600 hover:bg-red-50 transition-colors duration-200"
            style={{ border: 'none' }}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
